import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectEconomics } from '../reducers/economicsSlice';

import { selectUser } from '../reducers/userSlice';
import { chartColors } from '../helpers/chartColors';

export const EconomicsNPVChart = ({ cashflowType }) => {

  const user = useSelector(selectUser);
  const economics = useSelector(selectEconomics);
  const currentEconomics = economics?.byId?.[economics?.currentEconomicsId];

  const data = useMemo(() => getChartData(currentEconomics, cashflowType), [currentEconomics, cashflowType]);

  const darkMode = user?.value?.darkMode;

  const layout = {
    title: {
      text: 'Net Present Values',
      font: {
        size: 12
      }
    },
    yaxis: { // Main y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      title: {
        text: `Net Present Values ${currentEconomics?.settings?.currency === 'cad' ? '(CAD)' : '(USD)'}`,
        font: {size: 12}
      },
      tickfont: {size: 10},
      autorange: true,
      anchor: 'x'
    },
    xaxis: {
      rangemode: 'nonnegative',
      title: {
        text: 'Discount Rate (%)',
        standoff: 10,
        font: {size: 12}
      },
      tickfont: {size: 10},
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across"
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: -0.1
    },
    uirevision:'true',
    shapes: [ 
    ],
    margin: {"t": 50, "l": 50, "r": 50, "b": 50},
    images: [
      {
        x: 0.055,
        y: 1.02,
        sizex: 0.1,
        sizey: 0.075,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xanchor: "right",
        xref: "paper",
        yanchor: "bottom",
        yref: "paper",
        opacity: 1
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };


  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: currentEconomics?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 480,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%', minHeight: '500px', minWidth: '600px'}}
    />
  );

};

function getChartData(economics, cashflowType) {
  try {
    let data = [];
    const currency = economics?.settings?.currency;
    data = plotCashlow(data, economics, currency, cashflowType);
    return data;
  } catch (error) {
    console.log('Could not get chart data!');
    console.log(error);
    return null;
  };
};

function plotCashlow(data, economics, currency, cashflowType) {
  try {
    const X = [0, 5, 10, 15, 20, 25];
    const Y = economics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.cashflowBTax;
    const YATax = economics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.cashflowATax;
    let newData = [...data];

    newData.push({ // Plot well cash flow
      type: "scatter",
      mode: "line",
      x: X,
      y: Y,
      name: `NPV b/ Tax ${currency === 'usd' ? '(USD)' : '(CAD)'}`,
      marker: {color: chartColors?.[0]}
      // marker: {color: '#33aa33'}
    });

    newData.push({ // Plot well cash flow a/ tax
      type: "scatter",
      mode: "line",
      x: X,
      y: YATax,
      name: `NPV a/ Tax ${currency === 'usd' ? '(USD)' : '(CAD)'}`,
      marker: {color: chartColors?.[0]},
      line: {dash: 'dash'}
      // marker: {color: '#33aa33'}
    });
    return newData;
  } catch (error) {
    console.log(error);
    return data;
  };
};
